import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import { Spinner } from "components/ui";

interface BmsBookingFrameProps {
	// Calenso widget URL for booking a new appointment. BMS sends it as
	// `booking_link_appointment_iframe`, i.e. meant to be embedded.
	bookingLink: string;
}

// The Calenso booking widget, embedded in the appointments dialog (see
// Header/BmsAppointmentButton) instead of opened in a new tab: the endcustomer
// books without leaving the configurator, and returning to the list is one
// click rather than finding the right tab again.
//
// The spinner covers the frame until its first load: Calenso takes a moment,
// and an empty white box reads as broken.
const BmsBookingFrame: React.FC<BmsBookingFrameProps> = ({ bookingLink }) => {
	const { t } = useTranslation();
	const [loaded, setLoaded] = useState(false);

	return (
		<StyledFrameWrapper>
			{!loaded && (
				<StyledSpinnerOverlay>
					<Spinner />
				</StyledSpinnerOverlay>
			)}
			<StyledFrame
				src={bookingLink}
				title={String(t("app.BmsAppointments.panel.bookButton"))}
				onLoad={() => setLoaded(true)}
			/>
		</StyledFrameWrapper>
	);
};

// Tall enough for Calenso's slot picker without an inner scrollbar on a
// laptop, and capped by the viewport so the dialog's own head and footer stay
// on screen: 18rem covers the scrim's and dialog's padding, the title row and
// the footer.
const StyledFrameWrapper = styled.div`
	position: relative;
	height: min(760px, calc(100vh - 18rem));
	min-height: 420px;
	border: 1px solid var(--color-border);
	border-radius: 6px;
	overflow: hidden;
`;

const StyledFrame = styled.iframe`
	display: block;
	width: 100%;
	height: 100%;
	border: 0;
`;

const StyledSpinnerOverlay = styled.div`
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background: var(--color-surface);
`;

export default BmsBookingFrame;
